import { defineStore } from 'pinia';
import { useApi } from '@/lib/api';
import { useGlobalStore } from '@/store/global-store-base';
import { useUser } from '@/store/user';
import { GenderFlag } from '@/ApiClient.ts';

export const useMeasurements = defineStore('measurements', () => {
  const api = useApi();
  const globalStoreBase = useGlobalStore({
    load: api.measurement_GetMeasurements,
    dto: 'MeasurementDto',
    create: api.measurement_CreateMeasurement,
    update: api.measurement_UpdateMeasurement,
    delete: api.measurement_DeleteMeasurement,
    usage: api.measurement_GetMeasurementUsage,
    signalR: 'MeasurementUpdate',
  });

  async function updateGradings(
    measurementId: number,
    gradingsBySizeAndGender: Record<string, { sizeId: number; genders: number; gradingValue: number }>
  ) {
    const brandId = useUser().brandId;
    // Convert to two-dimensional dictionary format: { sizeId: { genders: value } }
    const gradingsBySizeAndGenderMap: Record<number, Record<number, number>> = {};
    Object.values(gradingsBySizeAndGender).forEach((item) => {
      if (!gradingsBySizeAndGenderMap[item.sizeId]) {
        gradingsBySizeAndGenderMap[item.sizeId] = {};
      }
      gradingsBySizeAndGenderMap[item.sizeId][item.genders] = item.gradingValue;
    });

    const result = await api.measurement_UpdateMeasurementGradings(brandId, measurementId, {
      gradingsBySizeAndGender: gradingsBySizeAndGenderMap,
    });
    // Update the measurement in the store
    const index = globalStoreBase.loaded.value.findIndex((m) => m.id === result.id);
    if (index !== -1) {
      globalStoreBase.loaded.value[index] = result;
    }
    return result;
  }

  function getGradings(measurementId: number, genderFlag: number): Map<number, number> {
    const measurement = globalStoreBase.byId.value[measurementId];
    if (!measurement) {
      return new Map();
    }

    // Filter gradings: try exact match first, fallback to None
    const matchedGradings = measurement.gradings.filter((g) => g.genders === genderFlag);
    const gradingsToUse =
      matchedGradings.length > 0 ? matchedGradings : measurement.gradings.filter((g) => g.genders === GenderFlag.None);

    return new Map<number, number>(gradingsToUse.map((g) => [g.sizeSystemVersionSizeId, g.gradingValue]));
  }

  return {
    ...globalStoreBase,
    updateGradings,
    getGradings,
  };
});
